import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Divider,
  Autocomplete,
  IconButton,
  Container
} from '@mui/material';
import {
  Save as SaveIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  ArrowBack as ArrowBackIcon,
  Assignment as PlanIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useUserData } from '../../hooks/useUserData';
import { createTreatmentPlan, updateTreatmentPlan, getCancerDiagnoses } from '../../services/oncologyService';
import { getChemotherapyRegimens } from '../../services/chemotherapyService';
import {
  TREATMENT_INTENTS,
  TREATMENT_INTENT_DESCRIPTIONS,
  RADIATION_TECHNIQUES
} from '../../types/enums';
import { PageHeader, PageContainer, ContentCard } from './oncologyStyles';

const steps = ['Plan Overview', 'Treatment Modalities', 'Goals & Timeline', 'Review & Submit'];

const TreatmentPlanBuilder = ({ patientId: propPatientId, diagnosisId: propDiagnosisId, existingPlan, onSave, onCancel, embedded = false }) => {
  const { patientId: urlPatientId, planId } = useParams();
  const navigate = useNavigate();
  const patientId = propPatientId || urlPatientId;
  const { defaultFacility, currentUser } = useUserData();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Diagnoses state for selection
  const [diagnoses, setDiagnoses] = useState([]);
  const [loadingDiagnoses, setLoadingDiagnoses] = useState(false);
  const [selectedDiagnosisId, setSelectedDiagnosisId] = useState(propDiagnosisId || existingPlan?.diagnosisId || '');
  
  // Chemotherapy regimens state (facility-specific)
  const [regimens, setRegimens] = useState([]);
  const [loadingRegimens, setLoadingRegimens] = useState(false);

  // Fetch patient's cancer diagnoses when patientId changes
  useEffect(() => {
    const fetchDiagnoses = async () => {
      if (!patientId || !defaultFacility?.id) return;
      
      try {
        setLoadingDiagnoses(true);
        const response = await getCancerDiagnoses(patientId, defaultFacility.id);
        setDiagnoses(response.data || []);
        
        // Auto-select if only one diagnosis exists and none is pre-selected
        if (response.data?.length === 1 && !selectedDiagnosisId) {
          setSelectedDiagnosisId(response.data[0].id);
        }
      } catch (err) {
        console.error('Error fetching diagnoses:', err);
      } finally {
        setLoadingDiagnoses(false);
      }
    };
    
    fetchDiagnoses();
  }, [patientId, defaultFacility?.id, selectedDiagnosisId]);

  // Fetch facility-specific chemotherapy regimens
  useEffect(() => {
    const fetchRegimens = async () => {
      if (!defaultFacility?.id) return;
      
      try {
        setLoadingRegimens(true);
        const response = await getChemotherapyRegimens(defaultFacility.id, { isActive: true });
        setRegimens(response.data || []);
      } catch (err) {
        console.error('Error fetching regimens:', err);
      } finally {
        setLoadingRegimens(false);
      }
    };
    
    fetchRegimens();
  }, [defaultFacility?.id]);

  const [formData, setFormData] = useState({
    planName: existingPlan?.planName || '',
    intent: existingPlan?.intent || '',
    modalities: existingPlan?.modalities || [],
    
    // Chemotherapy
    includeChemotherapy: existingPlan?.chemotherapy ? true : false,
    chemotherapy: existingPlan?.chemotherapy || {
      regimenId: '',
      regimenName: '',
      plannedCycles: 0,
      startDate: '',
      notes: ''
    },
    
    // Radiation
    includeRadiation: existingPlan?.radiation ? true : false,
    radiation: existingPlan?.radiation || {
      technique: '',
      totalDoseGy: 0,
      fractions: 0,
      dosePerFraction: 0,
      targetVolume: '',
      startDate: '',
      notes: ''
    },
    
    // Surgery
    includeSurgery: existingPlan?.surgery ? true : false,
    surgery: existingPlan?.surgery || {
      procedureName: '',
      plannedDate: '',
      surgeonName: '',
      notes: ''
    },
    
    // Goals & Timeline
    treatmentGoals: existingPlan?.treatmentGoals || [],
    plannedStartDate: existingPlan?.plannedStartDate?.split('T')[0] || '',
    estimatedEndDate: existingPlan?.estimatedEndDate?.split('T')[0] || '',
    
    // Oncologist
    primaryOncologist: existingPlan?.primaryOncologist || currentUser?.uid || '',
    notes: existingPlan?.notes || ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!selectedDiagnosisId) {
        setError('Please select a cancer diagnosis');
        setLoading(false);
        return;
      }

      if (!formData.planName) {
        setError('Please enter a treatment plan name');
        setLoading(false);
        return;
      }

      const planData = {
        patientId,
        diagnosisId: selectedDiagnosisId,
        facilityId: defaultFacility.id,
        planName: formData.planName,
        intent: formData.intent,
        modalities: formData.modalities,
        chemotherapy: formData.includeChemotherapy ? formData.chemotherapy : undefined,
        radiation: formData.includeRadiation ? formData.radiation : undefined,
        surgery: formData.includeSurgery ? formData.surgery : undefined,
        treatmentGoals: formData.treatmentGoals,
        plannedStartDate: formData.plannedStartDate,
        estimatedEndDate: formData.estimatedEndDate,
        primaryOncologist: formData.primaryOncologist,
        notes: formData.notes,
        status: 'Draft'
      };

      let result;
      if (existingPlan?.id) {
        result = await updateTreatmentPlan(existingPlan.id, planData);
      } else {
        result = await createTreatmentPlan(planData);
      }

      setSuccess(true);
      if (onSave) {
        onSave(result);
      } else {
        // Navigate back to treatment plans or patient oncology view
        setTimeout(() => {
          navigate(`/oncology/treatment-plans/${patientId}`);
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving treatment plan:', err);
      setError(err.message || 'Failed to save treatment plan');
    } finally {
      setLoading(false);
    }
  };

  const goalOptions = [
    'Cure',
    'Disease control',
    'Symptom relief',
    'Prolong survival',
    'Improve quality of life',
    'Reduce tumor burden',
    'Prevent recurrence',
    'Bridge to surgery',
    'Downstage tumor',
    'Maintain remission'
  ];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            {/* Diagnosis Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Cancer Diagnosis</InputLabel>
                <Select
                  value={selectedDiagnosisId}
                  label="Cancer Diagnosis"
                  onChange={(e) => setSelectedDiagnosisId(e.target.value)}
                  disabled={loadingDiagnoses || !!propDiagnosisId}
                >
                  {loadingDiagnoses ? (
                    <MenuItem value="" disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} /> Loading diagnoses...
                    </MenuItem>
                  ) : diagnoses.length === 0 ? (
                    <MenuItem value="" disabled>
                      No cancer diagnoses found for this patient
                    </MenuItem>
                  ) : (
                    diagnoses.map(diagnosis => (
                      <MenuItem key={diagnosis.id} value={diagnosis.id}>
                        <Box>
                          <Typography variant="body1">
                            {diagnosis.cancerType} - {diagnosis.primarySite}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Stage: {diagnosis.staging?.clinicalStage || `${diagnosis.staging?.tStage || ''}${diagnosis.staging?.nStage || ''}${diagnosis.staging?.mStage || ''}`} | 
                            Diagnosed: {new Date(diagnosis.diagnosisDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              {diagnoses.length === 0 && !loadingDiagnoses && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Please create a cancer diagnosis first before creating a treatment plan.
                  <Button 
                    size="small" 
                    sx={{ ml: 2 }}
                    onClick={() => navigate(`/oncology/diagnosis/new/${patientId}`)}
                  >
                    Create Diagnosis
                  </Button>
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Treatment Plan Name"
                value={formData.planName}
                onChange={(e) => handleChange('planName', e.target.value)}
                placeholder="e.g., Neoadjuvant AC-T for Stage IIA Breast Cancer"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Treatment Intent</InputLabel>
                <Select
                  value={formData.intent}
                  label="Treatment Intent"
                  onChange={(e) => handleChange('intent', e.target.value)}
                >
                  {TREATMENT_INTENTS.map(intent => (
                    <MenuItem key={intent} value={intent}>
                      <Box>
                        <Typography variant="body1">{intent}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {TREATMENT_INTENT_DESCRIPTIONS[intent]}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Select treatment modalities for this plan:
            </Typography>
            
            {/* Chemotherapy */}
            <Card variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.includeChemotherapy}
                      onChange={(e) => {
                        handleChange('includeChemotherapy', e.target.checked);
                        if (e.target.checked && !formData.modalities.includes('Chemotherapy')) {
                          handleChange('modalities', [...formData.modalities, 'Chemotherapy']);
                        } else if (!e.target.checked) {
                          handleChange('modalities', formData.modalities.filter(m => m !== 'Chemotherapy'));
                        }
                      }}
                    />
                  }
                  label={<Typography variant="h6">Chemotherapy</Typography>}
                />
                {formData.includeChemotherapy && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        options={regimens}
                        loading={loadingRegimens}
                        getOptionLabel={(option) => 
                          typeof option === 'string' 
                            ? option 
                            : `${option.name}${option.code ? ` (${option.code})` : ''}`
                        }
                        value={regimens.find(r => r.id === formData.chemotherapy.regimenId) || null}
                        onChange={(e, newValue) => {
                          if (newValue) {
                            handleNestedChange('chemotherapy', 'regimenId', newValue.id);
                            handleNestedChange('chemotherapy', 'regimenName', newValue.name);
                            // Auto-fill planned cycles from regimen if not already set
                            if (!formData.chemotherapy.plannedCycles && newValue.numberOfCycles) {
                              handleNestedChange('chemotherapy', 'plannedCycles', newValue.numberOfCycles);
                            }
                          } else {
                            handleNestedChange('chemotherapy', 'regimenId', '');
                            handleNestedChange('chemotherapy', 'regimenName', '');
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Regimen"
                            required
                            placeholder="Select from facility regimens"
                            helperText={regimens.length === 0 && !loadingRegimens ? "No regimens available. Create one in Chemotherapy Regimens." : ""}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingRegimens ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => {
                          const { key, ...restProps } = props;
                          return (
                            <Box component="li" key={key} {...restProps}>
                              <Box>
                                <Typography variant="body1">
                                  {option.name} {option.code && <Chip label={option.code} size="small" sx={{ ml: 1 }} />}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.cycleLengthDays} days/cycle × {option.numberOfCycles} cycles
                                  {option.drugs?.length > 0 && ` • ${option.drugs.length} drugs`}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        noOptionsText={loadingRegimens ? "Loading regimens..." : "No regimens found for this facility"}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Planned Cycles"
                        value={formData.chemotherapy.plannedCycles}
                        onChange={(e) => handleNestedChange('chemotherapy', 'plannedCycles', parseInt(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <DatePicker
                        label="Start Date"
                        value={formData.chemotherapy.startDate ? new Date(formData.chemotherapy.startDate) : null}
                        onChange={(date) => handleNestedChange('chemotherapy', 'startDate', date ? date.toISOString().split('T')[0] : '')}
                        slotProps={{
                          textField: { fullWidth: true, size: 'small' }
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>

            {/* Radiation */}
            <Card variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.includeRadiation}
                      onChange={(e) => {
                        handleChange('includeRadiation', e.target.checked);
                        if (e.target.checked && !formData.modalities.includes('Radiation Therapy')) {
                          handleChange('modalities', [...formData.modalities, 'Radiation Therapy']);
                        } else if (!e.target.checked) {
                          handleChange('modalities', formData.modalities.filter(m => m !== 'Radiation Therapy'));
                        }
                      }}
                    />
                  }
                  label={<Typography variant="h6">Radiation Therapy</Typography>}
                />
                {formData.includeRadiation && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Technique</InputLabel>
                        <Select
                          value={formData.radiation.technique}
                          label="Technique"
                          onChange={(e) => handleNestedChange('radiation', 'technique', e.target.value)}
                        >
                          {RADIATION_TECHNIQUES.map(tech => (
                            <MenuItem key={tech} value={tech}>{tech}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Total Dose (Gy)"
                        value={formData.radiation.totalDoseGy}
                        onChange={(e) => handleNestedChange('radiation', 'totalDoseGy', parseFloat(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Fractions"
                        value={formData.radiation.fractions}
                        onChange={(e) => handleNestedChange('radiation', 'fractions', parseInt(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Target Volume"
                        value={formData.radiation.targetVolume}
                        onChange={(e) => handleNestedChange('radiation', 'targetVolume', e.target.value)}
                        placeholder="e.g., Right breast + supraclavicular"
                      />
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>

            {/* Surgery */}
            <Card variant="outlined" sx={{ borderRadius: '12px' }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.includeSurgery}
                      onChange={(e) => {
                        handleChange('includeSurgery', e.target.checked);
                        if (e.target.checked && !formData.modalities.includes('Surgery')) {
                          handleChange('modalities', [...formData.modalities, 'Surgery']);
                        } else if (!e.target.checked) {
                          handleChange('modalities', formData.modalities.filter(m => m !== 'Surgery'));
                        }
                      }}
                    />
                  }
                  label={<Typography variant="h6">Surgery</Typography>}
                />
                {formData.includeSurgery && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Procedure Name"
                        value={formData.surgery.procedureName}
                        onChange={(e) => handleNestedChange('surgery', 'procedureName', e.target.value)}
                        placeholder="e.g., Lumpectomy with sentinel node biopsy"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <DatePicker
                        label="Planned Date"
                        value={formData.surgery.plannedDate ? new Date(formData.surgery.plannedDate) : null}
                        onChange={(date) => handleNestedChange('surgery', 'plannedDate', date ? date.toISOString().split('T')[0] : '')}
                        slotProps={{
                          textField: { fullWidth: true, size: 'small' }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Surgeon"
                        value={formData.surgery.surgeonName}
                        onChange={(e) => handleNestedChange('surgery', 'surgeonName', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={goalOptions}
                value={formData.treatmentGoals}
                onChange={(e, newValue) => handleChange('treatmentGoals', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Treatment Goals" placeholder="Add goals" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Planned Start Date"
                value={formData.plannedStartDate ? new Date(formData.plannedStartDate) : null}
                onChange={(date) => handleChange('plannedStartDate', date ? date.toISOString().split('T')[0] : '')}
                slotProps={{
                  textField: { fullWidth: true, size: 'small' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Estimated End Date"
                value={formData.estimatedEndDate ? new Date(formData.estimatedEndDate) : null}
                onChange={(date) => handleChange('estimatedEndDate', date ? date.toISOString().split('T')[0] : '')}
                slotProps={{
                  textField: { fullWidth: true, size: 'small' }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 3:
        const selectedDiagnosis = diagnoses.find(d => d.id === selectedDiagnosisId);
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Treatment Plan Summary</Typography>
            <Card variant="outlined" sx={{ mb: 2, borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">{formData.planName}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Intent: {formData.intent}
                </Typography>
                {selectedDiagnosis && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Linked Diagnosis:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedDiagnosis.cancerType} - {selectedDiagnosis.primarySite} 
                      {selectedDiagnosis.staging && ` (Stage: ${selectedDiagnosis.staging.clinicalStage || `${selectedDiagnosis.staging.tStage || ''}${selectedDiagnosis.staging.nStage || ''}${selectedDiagnosis.staging.mStage || ''}`})`}
                    </Typography>
                  </>
                )}
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Modalities:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {formData.modalities.map(m => (
                    <Chip key={m} label={m} size="small" color="primary" />
                  ))}
                </Box>
                {formData.treatmentGoals.length > 0 && (
                  <>
                    <Typography variant="subtitle2">Goals:</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {formData.treatmentGoals.map((g, i) => (
                        <Chip key={i} label={g} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </>
                )}
                {formData.plannedStartDate && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Timeline:</strong> {formData.plannedStartDate} to {formData.estimatedEndDate || 'TBD'}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  const stepperAndContent = (
    <>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 300 }}>
        {renderStepContent(activeStep)}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Treatment Plan'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<NextIcon />}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </>
  );

  // Show message if no patient selected (standalone mode)
  if (!patientId && !planId && !embedded) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          {/* Header with Back Navigation */}
          <PageHeader
            title="Treatment Plans"
            subtitle="Comprehensive cancer treatment planning"
            icon={<PlanIcon />}
            backTo="/oncology"
          />

          <ContentCard>
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please select a patient to create or view treatment plans.
              </Alert>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                To create a treatment plan, navigate to a patient's profile and use the Oncology tab.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/patients')}>
                Go to Patients
              </Button>
            </CardContent>
          </ContentCard>
        </Container>
      </PageContainer>
    );
  }

  // Embedded mode
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton onClick={() => onCancel ? onCancel() : navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <PlanIcon color="success" />
            <Typography variant="h6" fontWeight="bold">
              {existingPlan ? 'Edit Treatment Plan' : 'Create Treatment Plan'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Treatment plan saved successfully!
            </Alert>
          )}

          {stepperAndContent}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page with patient
  return (
    <PageContainer>
      <Container maxWidth="lg">
        {/* Header with Back Navigation */}
        <PageHeader
          title={existingPlan ? 'Edit Treatment Plan' : 'Create Treatment Plan'}
          subtitle="Comprehensive cancer treatment planning"
          icon={<PlanIcon />}
          backTo="/oncology"
        />

        <ContentCard>
          <CardContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Treatment plan saved successfully!
              </Alert>
            )}

            {stepperAndContent}
          </CardContent>
        </ContentCard>
      </Container>
    </PageContainer>
  );
};

export default TreatmentPlanBuilder;
